import {
  Button, Card, CardContent, Grid, Stack,
} from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import BlockData from '../../../models/BlockData';
import LinkData from '../../../models/LinkData';
import { URLTargetType } from '../../../models/URLTargetType';
import { URLOpenType } from '../../../models/URLOpenType';
import LinkComponent from '../../Link/LinkComponent';
import ModalComponent from '../../ModalComponent';
import BlockSettings, { BlockJustify, BlockSeparator } from '../../../models/BlockSettings';
import BlockJustifyPicker from '../../Modals/BlockSettingsModal/components/BlockJustifyPicker';
import BlockSeparatorPicker from '../../Modals/BlockSettingsModal/components/BlockSeparatorPicker';
import { FileContext } from '../../../contexts/FileContext';

export type BlockFooterMenuSettingsCloseHandler = (newItems?: LinkData[]) => void;

interface BlockFooterMenuSettingsProps {
  menuItems: LinkData[],
  settings: BlockSettings,
  open: boolean,
  blockData: BlockData[]
  closeHandler: BlockFooterMenuSettingsCloseHandler,
  updateHandler: (item: any) => void,
}

function BlockFooterMenuSettings({
  menuItems,
  settings,
  open,
  blockData,
  closeHandler,
  updateHandler,
}: BlockFooterMenuSettingsProps) {
  const [activeTab, setActiveTab] = useState<string>('itemsTab');
  const [direction, setDirection] = useState<'row' | 'column'>('row');
  const [footerSettings, setFooterSettings] = useState(settings);
  const [managedItems, setManagedItems] = useState<LinkData[]>(JSON.parse(JSON.stringify(menuItems)));
  const [error, setError] = useState('');
  const [firstLoad, setFirstLoad] = useState(true);
  const itemKeysRef = useRef(new WeakMap<LinkData, string>());
  const getItemKey = (link: LinkData) => {
    if (!itemKeysRef.current.has(link)) {
      itemKeysRef.current.set(link, uuid());
    }
    return itemKeysRef.current.get(link) as string;
  };
  const { getDownloaddableFileUrl, fetch } = useContext(FileContext);

  const handleClose = () => {
    setError('');
    setFirstLoad(true);
    closeHandler(null);
  };

  const handleSave = () => {
    if (!managedItems.find((item) => item.title === '')) {
      setError('');
      setFirstLoad(true);
      updateHandler(footerSettings);
      closeHandler(managedItems);
    } else {
      setError('Please enter a title');
    }
  };

  const handleAdd = () => {
    setManagedItems([...managedItems, LinkData.getDefault('New')]);
  };

  const handleChangeJustify = (value: BlockJustify) => {
    setFooterSettings({ ...footerSettings, justify: value });
  };

  const handleChangeSeparator = (value: BlockSeparator) => {
    setFooterSettings({ ...footerSettings, separator: value });
  };

  useEffect(() => {
    setFooterSettings(footerSettings);
  }, [footerSettings]);

  // PDEV3-34【2】: re-seed all local edits from props on (re)open so changes
  // discarded via Close don't carry over to the next open.
  useEffect(() => {
    if (open) {
      setFooterSettings(settings);
      setManagedItems(JSON.parse(JSON.stringify(menuItems)));
      setActiveTab('itemsTab');
      setError('');
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const items = useMemo(() => {
    if (managedItems.length > 0) {
      return managedItems.map((link, index) => (
        <div style={{ position: 'relative' }} key={getItemKey(link)}>
          <Card style={{ overflow: 'visible' }}>
            <CardContent>
              <LinkComponent
                link={link}
                blockData={blockData}
                firstLoad={firstLoad}
                onRemoveBlock={() => setManagedItems(managedItems.filter((item) => item !== link))}
                onChangeURLTitle={(newTitle) => { managedItems[index].title = newTitle; }}
                onChangeTargetType={(newTargetType) => {
                  if (newTargetType === URLTargetType.INTERNAL) {
                    managedItems[index].linkData.targetType = newTargetType;
                    managedItems[index].linkData.openType = URLOpenType.SAME_PAGE;
                  } else {
                    managedItems[index].linkData.targetType = newTargetType;
                  }
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onSelectBlock={(selected) => {
                  managedItems[index].linkData.url = selected;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onSelectUrl={(selected) => {
                  managedItems[index].linkData.url = selected;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onChangeLink={(newLink) => { managedItems[index].linkData.url = newLink; }}
                onChangeOpenType={(checked) => {
                  managedItems[index].linkData.openType = checked ? URLOpenType.NEW_TAB : URLOpenType.SAME_PAGE;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onChangeFile={(newFile) => {
                  managedItems[index].linkData.url = getDownloaddableFileUrl(newFile?.file_name);
                  setManagedItems([...managedItems]);
                }}
              />
            </CardContent>
          </Card>
        </div>
      ));
    }

    return [];
  }, [blockData, firstLoad, getDownloaddableFileUrl, managedItems]);

  useEffect(() => {
    if (!open) {
      setManagedItems(JSON.parse(JSON.stringify(menuItems)));
    }
  }, [menuItems, open]);

  useEffect(() => {
    fetch();
  }, []);

  return (
    <ModalComponent
      errorMessage={error}
      size="xl"
      title="Menu Settings"
      confirmButtonText="Apply"
      onConfirm={handleSave}
      show={open}
      closeModal={handleClose}
      centered
      animation={false}
      scrollable
    >
      <nav className="nav nav-tabs mb-3">
        <a
          className={`nav-link ${activeTab === 'itemsTab' ? 'active' : ''}`}
          href="#"
          onClick={() => setActiveTab('itemsTab')}
        >
          Menu List
        </a>
        <a
          className={`nav-link ${activeTab === 'settingsTab' ? 'active' : ''}`}
          href="#"
          onClick={() => setActiveTab('settingsTab')}
        >
          Style
        </a>
      </nav>
      <div className="tab-content">
        <div
          className={`tab-pane ${activeTab === 'itemsTab' ? 'active show' : ''}`}
        >
          <Stack spacing={4}>
            {items}
            <Stack key="add" spacing={0} sx={{ border: '1px dashed lightGray', p: 0 }}>
              <Button color="primary" onClick={handleAdd}>
                <AddCircleRoundedIcon />
              </Button>
            </Stack>
          </Stack>
        </div>
        <div
          className={`tab-pane ${activeTab === 'settingsTab' ? 'active show' : ''}`}
        >
          {/* MenuPositioningSettings Component  */}
          <Stack spacing={4}>
            <div>
              <Grid
                container
                direction="column"
                alignItems="start"
                justifyContent="center"
                sx={{
                  p: 2,
                  border: '1px dashed lightGray',
                  position: 'relative',
                }}
              >
                <Grid item sx={{ my: 1 }}>
                  <BlockJustifyPicker
                    isFooter
                    value={footerSettings?.justify}
                    direction={direction}
                    onChangeJustify={(value) => {
                      handleChangeJustify(value);
                    }}
                  />
                </Grid>

                <Grid item sx={{ my: 1 }}>
                  <BlockSeparatorPicker
                    value={footerSettings?.separator}
                    color={footerSettings?.separatorColor}
                    onChangeSeparator={(value) => {
                      handleChangeSeparator(value);
                    }}
                    onChangeSeparatorColor={(color) => {
                      setFooterSettings({ ...footerSettings, separatorColor: color });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Stack>
        </div>
      </div>
    </ModalComponent>
  );
}

export default BlockFooterMenuSettings;
